import React, { useState } from 'react';
import { ArrowLeft, Trash2, Plus } from 'lucide-react';
import { DatePlannerState } from '../types';
import { StorageService } from '../services/storage';
import { NEIGHBORHOODS } from '../data/places';

interface PlannerProps {
  onGenerate: (state: DatePlannerState) => void;
  initialState: DatePlannerState;
}

const VIBES = [
  '❤️ Romantic',
  '🌆 City Exploration',
  '🌿 Peaceful',
  '🎨 Arts & Culture',
  '🍜 Food Adventure',
  '📸 Photography',
  '🎢 Fun & Activities',
  '🌅 Sunset',
  '📚 Intellectual',
  '🎭 Cultural',
  '💸 Budget Date',
  '✨ Surprise Date',
];

const DURATIONS = [
  { label: '3 hours', value: 180 },
  { label: '5 hours', value: 300 },
  { label: '7 hours', value: 420 },
  { label: 'Full day', value: 600 },
];

const TRANSPORT_MODES = [
  { id: 'metro', label: '🚇 Metro' },
  { id: 'local_train', label: '🚆 Local Train' },
  { id: 'bus', label: '🚌 Bus' },
  { id: 'walking', label: '🚶 Walking' },
  { id: 'ferry', label: '⛴️ Ferry' },
  { id: 'auto', label: '🛺 Auto' },
];

export default function Planner({ onGenerate, initialState }: PlannerProps) {
  const [state, setState] = useState<DatePlannerState>(initialState);
  const [excludedPlaces, setExcludedPlaces] = useState<string[]>(state.excludedPlaces);
  const [showExcludedInput, setShowExcludedInput] = useState(false);
  const [excludedInput, setExcludedInput] = useState('');

  const handleStateChange = (updates: Partial<DatePlannerState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const toggleVibe = (vibe: string) => {
    setState(prev => ({
      ...prev,
      vibes: prev.vibes.includes(vibe)
        ? prev.vibes.filter(v => v !== vibe)
        : [...prev.vibes, vibe]
    }));
  };

  const toggleTransport = (mode: string) => {
    setState(prev => ({
      ...prev,
      transportModes: prev.transportModes.includes(mode)
        ? prev.transportModes.filter(m => m !== mode)
        : [...prev.transportModes, mode]
    }));
  };

  const addExcludedPlace = () => {
    if (excludedInput.trim()) {
      setExcludedPlaces([...excludedPlaces, excludedInput.trim()]);
      setExcludedInput('');
      setState(prev => ({
        ...prev,
        excludedPlaces: [...prev.excludedPlaces, excludedInput.trim()]
      }));
    }
  };

  const removeExcludedPlace = (place: string) => {
    setExcludedPlaces(excludedPlaces.filter(p => p !== place));
    setState(prev => ({
      ...prev,
      excludedPlaces: prev.excludedPlaces.filter(p => p !== place)
    }));
  };

  const handleGenerate = () => {
    if (!state.startTime || !state.date) {
      alert('Please fill in all required fields');
      return;
    }
    onGenerate(state);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-4 flex items-center gap-3 z-10">
        <button className="p-2 hover:bg-slate-100 rounded-lg">
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-xl font-bold text-slate-900">Plan Our Date</h1>
      </div>

      {/* Content */}
      <div className="px-4 pb-32">
        {/* Starting Point */}
        <div className="mt-6 mb-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            📍 Starting Point
          </label>
          <select
            value={state.startLocation}
            onChange={(e) => handleStateChange({ startLocation: e.target.value })}
            className="input-field"
          >
            {NEIGHBORHOODS.map(nb => (
              <option key={nb.name} value={nb.name}>{nb.name}</option>
            ))}
            <option value="custom">Custom Location</option>
          </select>
        </div>

        {/* Ending Location */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            🏠 Return To
          </label>
          <select
            value={state.endLocation}
            onChange={(e) => handleStateChange({ endLocation: e.target.value })}
            className="input-field"
          >
            {NEIGHBORHOODS.map(nb => (
              <option key={nb.name} value={nb.name}>{nb.name}</option>
            ))}
          </select>
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              📅 Date
            </label>
            <input
              type="date"
              value={state.date}
              onChange={(e) => handleStateChange({ date: e.target.value })}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2">
              🕐 Start Time
            </label>
            <input
              type="time"
              value={state.startTime}
              onChange={(e) => handleStateChange({ startTime: e.target.value })}
              className="input-field"
            />
          </div>
        </div>

        {/* Duration */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            ⏱️ How Long?
          </label>
          <div className="grid grid-cols-2 gap-2">
            {DURATIONS.map(d => (
              <button
                key={d.value}
                onClick={() => handleStateChange({ duration: d.value })}
                className={`py-3 px-4 rounded-xl font-medium transition-all ${
                  state.duration === d.value
                    ? 'bg-rose-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-900 hover:border-rose-300'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        {/* Budget */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            💰 Budget Per Person
          </label>
          <div className="card bg-rose-50 border border-rose-200">
            <input
              type="range"
              min="300"
              max="2000"
              step="100"
              value={state.budget}
              onChange={(e) => handleStateChange({ budget: parseInt(e.target.value) })}
              className="w-full"
            />
            <div className="mt-3 text-center">
              <div className="text-3xl font-bold text-rose-600">₹{state.budget}</div>
              <div className="text-sm text-slate-600">For {state.numPeople} people</div>
            </div>
          </div>
        </div>

        {/* Transport */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            🚇 Preferred Transport
          </label>
          <div className="grid grid-cols-2 gap-2">
            {TRANSPORT_MODES.map(mode => (
              <button
                key={mode.id}
                onClick={() => toggleTransport(mode.id)}
                className={`py-3 px-3 rounded-xl font-medium transition-all text-sm ${
                  state.transportModes.includes(mode.id)
                    ? 'bg-rose-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-900'
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>

        {/* Vibes */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            ✨ Date Vibes (Select Multiple)
          </label>
          <div className="grid grid-cols-2 gap-2">
            {VIBES.map(vibe => (
              <button
                key={vibe}
                onClick={() => toggleVibe(vibe)}
                className={`py-2 px-3 rounded-lg font-medium transition-all text-xs text-center ${
                  state.vibes.includes(vibe)
                    ? 'bg-rose-100 text-rose-900 border border-rose-300'
                    : 'bg-white border border-slate-200 text-slate-700'
                }`}
              >
                {vibe}
              </button>
            ))}
          </div>
        </div>

        {/* Excluded Places */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-semibold text-slate-900">
              ❌ Places We've Done
            </label>
            <button
              onClick={() => setShowExcludedInput(!showExcludedInput)}
              className="p-1 hover:bg-slate-100 rounded-lg"
            >
              <Plus size={18} className="text-rose-600" />
            </button>
          </div>

          {showExcludedInput && (
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={excludedInput}
                onChange={(e) => setExcludedInput(e.target.value)}
                placeholder="e.g., Metcalfe Hall"
                className="input-field flex-1"
                onKeyDown={(e) => e.key === 'Enter' && addExcludedPlace()}
              />
              <button
                onClick={addExcludedPlace}
                className="btn-primary px-4"
              >
                Add
              </button>
            </div>
          )}

          {excludedPlaces.length > 0 && (
            <div className="space-y-2">
              {excludedPlaces.map(place => (
                <div key={place} className="flex items-center justify-between bg-slate-100 p-3 rounded-lg">
                  <span className="text-sm text-slate-900">{place}</span>
                  <button
                    onClick={() => removeExcludedPlace(place)}
                    className="p-1 hover:bg-slate-200 rounded"
                  >
                    <Trash2 size={16} className="text-slate-600" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Indoor/Outdoor */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-slate-900 mb-3">
            🏞️ Indoor / Outdoor
          </label>
          <div className="grid grid-cols-3 gap-2">
            {['any', 'indoor', 'outdoor'].map(type => (
              <button
                key={type}
                onClick={() => handleStateChange({ indoorOutdoor: type as any })}
                className={`py-3 px-4 rounded-xl font-medium transition-all ${
                  state.indoorOutdoor === type
                    ? 'bg-rose-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-900'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Generate Button */}
      <div className="fixed bottom-20 left-0 right-0 px-4 bg-gradient-to-t from-white to-transparent pt-4">
        <button
          onClick={handleGenerate}
          className="w-full btn-primary py-4 text-lg font-bold"
        >
          ✨ BUILD MY DATE
        </button>
      </div>
    </div>
  );
}
